import { useState } from 'react'
import { X } from 'lucide-react'
import { summarizeActionFlowStateTopology } from '../lib/actionflow-state-topology'
import { usePreviewTestingStore } from '../store/preview-testing-store'
import type { ActionFlowTraceEntry } from '../types'
import { buildActionExecutionModel } from '@/features/flow-editor/services/actionflow-execution-model'

function compactValue(value: unknown, maxLength = 96): string {
  let text: string
  if (value === undefined) text = 'undefined'
  else if (typeof value === 'string') text = value
  else {
    try {
      text = JSON.stringify(value)
    } catch {
      text = String(value)
    }
  }
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text
}

function traceStatusPresentation(status: ActionFlowTraceEntry['status']): {
  label: string
  summary: string
  textClass: string
  panelClass: string
} {
  switch (status) {
    case 'pass':
      return {
        label: 'pass',
        summary: 'Action verified in Preview',
        textClass: 'text-success',
        panelClass: 'border-success/25 bg-success/10 text-success',
      }
    case 'deduplicated':
      return {
        label: 'duplicate joined',
        summary: 'Duplicate run joined an existing execution',
        textClass: 'text-muted-foreground',
        panelClass: 'border-border bg-muted/40 text-foreground',
      }
    case 'stopped':
      return {
        label: 'stopped',
        summary: 'Action stopped by the flow',
        textClass: 'text-warning',
        panelClass: 'border-warning/25 bg-warning/10 text-warning',
      }
    case 'cancelled':
      return {
        label: 'cancelled',
        summary: 'Action cancelled by the host',
        textClass: 'text-warning',
        panelClass: 'border-warning/25 bg-warning/10 text-warning',
      }
    case 'timed-out':
      return {
        label: 'timed out',
        summary: 'Action exceeded its execution timeout',
        textClass: 'text-destructive',
        panelClass: 'border-destructive/25 bg-destructive/10 text-destructive',
      }
    case 'error':
      return {
        label: 'error',
        summary: 'Action needs review',
        textClass: 'text-destructive',
        panelClass: 'border-destructive/25 bg-destructive/10 text-destructive',
      }
  }
}

/**
 * Renders a single ActionFlow trace's evidence panel.
 *
 * By default it shows the newest LIVE-PREVIEW trace (actionFlowTraces[0]) — the
 * back-compat behavior for the canvas-anchored live overlay. The dry-run
 * results panel passes an explicit `trace` (the store's dedicated dryRunTrace
 * slot) so it renders THAT flow's dry-run, never the global newest preview
 * trace — otherwise a real preview run that DID touch live state could appear
 * under the panel's 'live state untouched' header (L4-CORR-2 / L4-CA-03).
 */
interface ActionFlowPreviewOverlayProps {
  trace?: ActionFlowTraceEntry | null
  testId?: string
  rendererProjectionTestId?: string
  title?: string
}

export function ActionFlowPreviewOverlay({
  trace,
  testId = 'actionflow-inspector',
  rendererProjectionTestId = 'actionflow-overlay-renderer-projection',
  title = 'ActionFlow Runtime',
}: ActionFlowPreviewOverlayProps) {
  // Always subscribe to the store so the live overlay re-renders on new traces;
  // an explicitly-passed trace (dry-run panel) overrides the global selection.
  const newestPreview = usePreviewTestingStore((s) => s.actionFlowTraces[0])
  const latest = trace !== undefined ? trace : newestPreview
  const [dismissedEntryId, setDismissedEntryId] = useState<string | null>(null)

  if (!latest || latest.id === dismissedEntryId) return null

  const visibleDiffs = latest.stateDiffs.slice(0, 4)
  const visibleActions = latest.actionCalls.slice(0, 3)
  const visibleSteps = (latest.stepTrace ?? []).slice(0, 4)
  const blockedScope = latest.blockedScope ?? latest.stateDomains[0] ?? 'unknown'
  const traceLabel = `${latest.contractVersion} / ${latest.traceId}`
  const domainsLabel = latest.stateDomains.length > 0 ? latest.stateDomains.join(', ') : 'none'
  const executionLanes = Array.from(
    new Set(latest.actionCalls.map((call) => buildActionExecutionModel(call.action).lane)),
  )
  const executionLanesLabel = executionLanes.length > 0 ? executionLanes.join(', ') : 'none'
  const topologySummary = summarizeActionFlowStateTopology(latest)
  const statusPresentation = traceStatusPresentation(latest.status)
  const interrupted = latest.status === 'error'
    || latest.status === 'cancelled'
    || latest.status === 'timed-out'
  const interruptedTitle = latest.status === 'timed-out'
    ? 'Action timed out'
    : latest.status === 'cancelled'
      ? 'Action cancelled'
      : 'Action failed'

  return (
    <aside
      data-testid={testId}
      className="pointer-events-auto absolute bottom-4 right-4 z-30 max-h-[calc(100%-2rem)] w-[min(420px,calc(100%-2rem))] overflow-y-auto overscroll-contain rounded-md border border-border bg-background/95 p-3 text-xs shadow-lg backdrop-blur"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-foreground">{title}</span>
        <div className="flex items-center gap-1.5">
          <span className={statusPresentation.textClass}>
            {statusPresentation.label}
          </span>
          <button
            type="button"
            aria-label="Dismiss ActionFlow evidence"
            className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => setDismissedEntryId(latest.id)}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className={`mt-2 rounded-md border px-2 py-1.5 ${statusPresentation.panelClass}`}>
        <div className="font-medium">{statusPresentation.summary}</div>
        <div className="mt-0.5 studio-ellipsis-block font-mono text-[10px] opacity-80" title={`${latest.eventLabel} -> ${latest.flowName}`}>
          {latest.eventLabel} {'->'} {latest.flowName}
        </div>
      </div>
      <div
        className="mt-2 rounded-md border border-border bg-card/70 px-2 py-1.5"
        data-testid="actionflow-state-topology-summary"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium text-foreground">State topology</div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {topologySummary.changedPathCount} paths · {latest.durationMs}ms
          </div>
        </div>
        <dl className="mt-1 grid grid-cols-[84px_minmax(0,1fr)] gap-x-2 gap-y-1">
          {topologySummary.rows.map((row) => (
            <div key={row.id} className="contents">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="studio-ellipsis-block font-mono text-foreground" title={row.value}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="mt-2 grid grid-cols-[64px_minmax(0,1fr)] gap-x-2 gap-y-1">
        <span className="text-muted-foreground">Trace</span>
        <span className="studio-ellipsis-block font-mono text-foreground" title={traceLabel}>
          {traceLabel}
        </span>
        {latest.executionId ? (
          <>
            <span className="text-muted-foreground">Execution</span>
            <span className="studio-ellipsis-block font-mono text-foreground" title={latest.executionId}>
              {latest.executionId}
            </span>
          </>
        ) : null}
        {latest.idempotencyKey ? (
          <>
            <span className="text-muted-foreground">Idempotency</span>
            <span className="studio-ellipsis-block font-mono text-foreground" title={latest.idempotencyKey}>
              {latest.idempotencyKey}
            </span>
          </>
        ) : null}
        {latest.duplicateOfExecutionId ? (
          <>
            <span className="text-muted-foreground">Joined</span>
            <span className="studio-ellipsis-block font-mono text-foreground" title={latest.duplicateOfExecutionId}>
              {latest.duplicateOfExecutionId}
            </span>
          </>
        ) : null}
        <span className="text-muted-foreground">Event</span>
        <span className="studio-ellipsis-block font-mono text-foreground" title={latest.eventLabel}>
          {latest.eventLabel}
        </span>
        <span className="text-muted-foreground">Flow</span>
        <span className="studio-ellipsis-block font-mono text-foreground" title={latest.flowName}>
          {latest.flowName}
        </span>
        {latest.eventValue !== undefined ? (
          <>
            <span className="text-muted-foreground">event.value</span>
            <span className="studio-ellipsis-block font-mono text-foreground" title={compactValue(latest.eventValue, 240)}>
              {compactValue(latest.eventValue)}
            </span>
          </>
        ) : null}
        <span className="text-muted-foreground">Execution lanes</span>
        <span className="studio-ellipsis-block font-mono text-foreground" title={executionLanesLabel}>
          {executionLanesLabel}
        </span>
        <span className="text-muted-foreground">State domains</span>
        <span className="studio-ellipsis-block font-mono text-foreground" title={domainsLabel}>
          {domainsLabel}
        </span>
      </div>
      {interrupted ? (
        <div className={`mt-2 rounded border px-2 py-1.5 text-[11px] ${statusPresentation.panelClass}`}>
          <div className="font-medium">{interruptedTitle}</div>
          <div className="mt-1 font-mono break-all">
            {latest.error ?? 'Unknown ActionFlow runtime error'}
          </div>
          <div className="mt-1 text-muted-foreground">
            Blocked state scope: <span className="font-mono text-foreground">{blockedScope}</span>
          </div>
        </div>
      ) : null}
      {latest.caughtError ? (
        <div className="mt-2 rounded border border-warning/30 bg-warning/5 px-2 py-1.5 text-[11px]">
          <span className="font-medium text-warning">Caught error</span>
          <span className="ml-1 font-mono text-warning break-all">{compactValue(latest.caughtError)}</span>
        </div>
      ) : null}
      {visibleSteps.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="actionflow-overlay-step-trace">
          <div className="text-muted-foreground">Step order</div>
          {visibleSteps.map((step, index) => (
            <div key={`${index}:${step.index}:${step.path}`} className="grid grid-cols-[72px_minmax(0,1fr)] gap-2 rounded bg-muted/40 px-2 py-1">
              <span className="font-mono text-foreground break-all">
                #{step.index}
                {step.parentIndex !== undefined ? ` <- ${step.parentIndex}` : ''}
              </span>
              <span className="font-mono text-muted-foreground break-all">
                {step.action ?? step.type} / {step.status}
                {step.detail ? ` / detail: ${compactValue(step.detail)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      {visibleActions.length > 0 && (
        <div className="mt-2 space-y-1">
          {visibleActions.map((call, index) => (
            <div key={`${index}:${call.path}:${call.action}`} className="grid grid-cols-[112px_minmax(0,1fr)] gap-2 rounded bg-muted/40 px-2 py-1">
              <span className="font-mono text-foreground break-all">
                {call.order !== undefined ? `#${call.order} ` : ''}{call.action}
              </span>
              <span className="font-mono text-muted-foreground break-all">
                {call.stepType} / {call.dataSource === 'unknown' ? buildActionExecutionModel(call.action).lane : call.dataSource}
                {call.parentIndex !== undefined ? ` / parentIndex: ${call.parentIndex}` : ''}
                {call.nestedFlow ? ` / nested callFlow: ${call.nestedFlow}` : ''}
                {call.operation ? ` / operation: ${call.operation}` : ''}
                {call.target ? ` / ${call.target}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      {visibleDiffs.length > 0 && (
        <div className="mt-2 space-y-1">
          <div className="text-muted-foreground">State diff reflected in Preview</div>
          {visibleDiffs.map((diff, index) => (
            <div key={`${index}:${diff.path}`} className="grid grid-cols-[112px_minmax(0,1fr)] gap-2 rounded bg-muted/40 px-2 py-1">
              <span className="font-mono text-foreground break-all">{diff.path}</span>
              <span className="font-mono text-muted-foreground break-all">
                {diff.domain}: {compactValue(diff.before)} {'->'} {compactValue(diff.after)}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <div className="text-muted-foreground">_results</div>
          <pre className="mt-1 max-h-16 overflow-auto rounded bg-muted/30 p-1 font-mono text-[10px]">
            {JSON.stringify(latest.results)}
          </pre>
        </div>
        <div>
          <div className="text-muted-foreground">Computed</div>
          <pre className="mt-1 max-h-16 overflow-auto rounded bg-muted/30 p-1 font-mono text-[10px]">
            {JSON.stringify(latest.computed)}
          </pre>
        </div>
      </div>
      {latest.rendererProjection.length > 0 && (
        <div className="mt-2">
          <div className="text-muted-foreground">Renderer</div>
          <div
            data-testid={rendererProjectionTestId}
            className="mt-1 max-h-20 space-y-1 overflow-auto"
          >
            {latest.rendererProjection.slice(0, 8).map((projection) => (
              <div
                key={`${projection.elementId}:${projection.text}`}
                className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 rounded bg-muted/30 px-2 py-1"
              >
                <span className="font-mono text-[10px] text-muted-foreground break-all">
                  {projection.elementId}
                </span>
                <span className="font-mono text-[10px] text-foreground break-all">
                  {projection.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  )
}
